import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, ShowProps, useDataProvider, useNotify } from "react-admin";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { filter, find, map, orderBy, sumBy } from "lodash";
import moment from "moment";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button as MuiButton,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SanitizedGrid from "../../../components/SanitizedGrid";
import IconWarning from "@material-ui/icons/Warning";
import AddParentOrPicButton from "./AddParentOrPicButton";
import IconSave from "@material-ui/icons/Save";
import IconDelete from "@material-ui/icons/Delete";
import { GridinRow, GridinRowsWithSpace, GridLabel } from "utils/style";

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      minWidth: 650,
      padding: "1rem",
    },
    input: {
      border: "1px solid #a6a6a6",
      padding: "5px 10px",
      borderRadius: 5,
    },
    pageWarning: {
      justifyContent: "center",
      alignItems: "center",
      display: "flex",
      color: "#f44336",
    },
    accordionLabel: {
      marginTop: "5px",
    },
    MuiAccordionSummary: {
      "& .MuiAccordionSummary-content": {
        margin: "15px 0px 5px",
      },
    },
  }),
);

const CcaSplitPaymentTable = (props: ShowProps) => {
  const [paymentPercentages, setPaymentPercentages] = useState<
    IPaymentPercentage[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [removedSplitPaymentItem, setRemovedSplitPaymentItem] =
    useState<IPaymentPercentage>(null);
  const [isShowUndoAlert, setIsShowUndoAlert] = useState<boolean>(false);
  const undoAlertTimeout = useRef(null);

  const provider = useDataProvider();
  const classes = useStyles();
  const notify = useNotify();
  const { id } = props;

  const getPaymentPercentages = () => {
    setLoading(true);
    provider
      .customGetListWithStaticParams(
        `students/${id}/paymentPercentages?Query.Filter=${JSON.stringify({})}`,
      )
      .then(({ data }: any) => {
        const splitPaymentPercentages = (data ?? []) as IPaymentPercentage[];

        // Filter splitPaymentPercentages where 'resource' contains "Payment"
        const filteredPaymentPercentages = splitPaymentPercentages.filter(
          (item) =>
            item?.resource
              ?.split(",") // Split by comma
              .map((resource) => resource.trim()) // Trim spaces
              .includes("CcaPayment"), // Check for resource
        );

        const sortedPaymentPercentages = orderBy(filteredPaymentPercentages, [
          "createdAt",
        ]);

        setPaymentPercentages(sortedPaymentPercentages);
        setLoading(false);
      })
      .catch((err: any) => {
        notify(
          `Cannot get CCA payment percentage, ${err.message}. Please try again`,
          "warning",
        );
        setLoading(false);
      });
  };

  useEffect(() => {
    getPaymentPercentages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveNewPaymentPercentageLocally = useCallback(
    (data: IParent) => {
      const validateDuplicateParentOrPic = find(paymentPercentages, {
        parentId: data.id,
      });
      const validateSameCompany = filter(paymentPercentages, (o) => {
        return (
          o.parent.company?.id === data.company?.id &&
          o.parent.type !== "parent"
        );
      });

      if (validateDuplicateParentOrPic) {
        notify("Cannot add this Parent/PIC twice", "warning");
        return;
      }

      if (validateSameCompany.length !== 0) {
        notify("Cannot add PIC from same company twice", "warning");
        return;
      }

      setPaymentPercentages(
        orderBy(
          [
            ...paymentPercentages,
            {
              createdAt: moment().toString(),
              id: 0,
              isMain: false,
              parentId: data.id,
              parent: data,
              ccaPaymentPercentage: 0,
              studentId: Number(id),
              resource: "CcaPayment",
              updatedAt: moment().toString(),
            },
          ],
          ["createdAt"],
        ),
      );
    },
    [paymentPercentages, id],
  );

  const changeRowPercentage = useCallback(
    (value, parentId) => {
      const newPaymentPercentages = [...paymentPercentages];
      const updateTarget = find(newPaymentPercentages, { parentId });
      if (value >= 100) {
        updateTarget.ccaPaymentPercentage = 100;
      } else if (value <= 0) {
        updateTarget.ccaPaymentPercentage = 0;
      } else {
        updateTarget.ccaPaymentPercentage = Number(value);
      }
      setPaymentPercentages(newPaymentPercentages);
    },
    [paymentPercentages],
  );

  const undoRemovePaymentPercentageItem = useCallback(() => {
    if (removedSplitPaymentItem) {
      setPaymentPercentages(
        orderBy(
          [...paymentPercentages, removedSplitPaymentItem],
          ["createdAt"],
        ),
      );
      setRemovedSplitPaymentItem(null);
      setIsShowUndoAlert(false);
      notify("Action undone");

      if (undoAlertTimeout.current) {
        clearTimeout(undoAlertTimeout.current); // Clear timeout when undo is clicked
      }
    }
  }, [removedSplitPaymentItem]);

  const removePaymentPercentageItem = useCallback(
    (parentId) => {
      // Clear any existing timeout before setting a new one
      if (undoAlertTimeout.current) {
        clearTimeout(undoAlertTimeout.current);
      }

      const removedItem = paymentPercentages.find(
        (item) => item.parentId === parentId,
      );
      setRemovedSplitPaymentItem(removedItem);

      const newPaymentPercentages = filter(
        paymentPercentages,
        (o: IPaymentPercentage) => {
          return o.parentId !== parentId;
        },
      );

      setPaymentPercentages(newPaymentPercentages);
      setIsShowUndoAlert(true);

      // Start new timeout
      undoAlertTimeout.current = setTimeout(() => {
        setIsShowUndoAlert(false);
      }, 5000);
    },
    [paymentPercentages],
  );

  const allowSave = useMemo(() => {
    const totalPercentage = sumBy(paymentPercentages, "ccaPaymentPercentage");

    return totalPercentage === 100;
  }, [paymentPercentages]);

  const savePaymentPercentage = useCallback(async () => {
    const formatted = map(paymentPercentages, (o: IPaymentPercentage) => {
      return {
        id: o.id === 0 ? undefined : o.id,
        parentId: o.parentId,
        studentId: Number(id),
        ccaPaymentPercentage: o.ccaPaymentPercentage,
      };
    });

    try {
      await provider.customUpdate(`students/${id}/paymentPercentages`, {
        data: { parentStudentPaymentPercentages: formatted },
      });
      getPaymentPercentages();
      notify("Split payment data updated successfully", "success");
    } catch (error) {
      notify(
        "Cannot save updated payment percentage, please try again",
        "warning",
      );
    }
  }, [paymentPercentages, id, getPaymentPercentages]);

  // Clear timeout when component unmounts (cleanup function)
  useEffect(() => {
    return () => {
      if (undoAlertTimeout.current) {
        clearTimeout(undoAlertTimeout.current);
      }
    };
  }, []);

  return (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="cca-split-payment-content"
        id="cca-split-payment-header"
        className={classes.MuiAccordionSummary}
      >
        <h3 className={classes.accordionLabel}>CCA Split Payment</h3>
      </AccordionSummary>
      <AccordionDetails>
        <SanitizedGrid container spacing={2}>
          <Grid item xs={12} style={GridinRowsWithSpace}>
            <div /> {/* Empty div for spacing to keep text in center */}
            {!allowSave && !loading && (
              <span className={classes.pageWarning}>
                <IconWarning /> Sum of All Payment Account CCA Split Payment is
                not 100%
              </span>
            )}
            <div>
              <AddParentOrPicButton
                onSubmit={(data: IParent) => {
                  saveNewPaymentPercentageLocally(data);
                }}
              />
              <Button
                disabled={!allowSave}
                onClick={savePaymentPercentage}
                label="Save"
              >
                <IconSave />
              </Button>
            </div>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>Payment Account</TableCell>
                    <TableCell>Percentage (%)</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentPercentages.length > 0 ? (
                    <>
                      {paymentPercentages.map(
                        (
                          paymentPercentage: IPaymentPercentage,
                          key: number,
                        ) => {
                          return (
                            <TableRow key={`percentage-${key}`}>
                              <TableCell>
                                <SanitizedGrid container spacing={2}>
                                  <Grid item xs={12} style={GridinRow}>
                                    <Typography
                                      variant="body2"
                                      style={GridLabel}
                                    >
                                      Id :{" "}
                                      {paymentPercentage.parent
                                        ? `${paymentPercentage.parent.id}`
                                        : "-"}
                                    </Typography>
                                  </Grid>

                                  <Grid item xs={12} style={GridinRow}>
                                    <Typography
                                      variant="body2"
                                      style={GridLabel}
                                    >
                                      Type :{" "}
                                      {paymentPercentage.parent.company
                                        ? `company`
                                        : paymentPercentage.parent.type}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} style={GridinRow}>
                                    <Typography
                                      variant="body2"
                                      style={GridLabel}
                                    >
                                      Name :{" "}
                                      {paymentPercentage.parent
                                        ? `${paymentPercentage.parent.firstName} ${paymentPercentage.parent.lastName}`
                                        : "-"}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} style={GridinRow}>
                                    <Typography
                                      variant="body2"
                                      style={GridLabel}
                                    >
                                      Email :{" "}
                                      {paymentPercentage.parent
                                        ? `${
                                            paymentPercentage.parent?.email ||
                                            paymentPercentage.parent?.user
                                              ?.email ||
                                            "-"
                                          }`
                                        : "-"}
                                    </Typography>
                                  </Grid>
                                </SanitizedGrid>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  type="number"
                                  className={classes.input}
                                  value={paymentPercentage.ccaPaymentPercentage}
                                  onChange={(e) => {
                                    changeRowPercentage(
                                      e.target.value,
                                      paymentPercentage.parentId,
                                    );
                                  }}
                                ></TextField>
                              </TableCell>
                              <TableCell>
                                {paymentPercentage.isMain === false && (
                                  <Button
                                    onClick={() =>
                                      removePaymentPercentageItem(
                                        paymentPercentage.parentId,
                                      )
                                    }
                                    label="Remove"
                                    style={{ backgroundColor: "#f44336" }}
                                  >
                                    <IconDelete />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        },
                      )}
                    </>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4}>
                        {loading ? "Loading..." : "No results found."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </SanitizedGrid>

        <Snackbar
          open={isShowUndoAlert}
          message="CCA split payment account removed"
          action={
            <MuiButton
              style={{ color: "#E0E0E0" }}
              onClick={undoRemovePaymentPercentageItem}
            >
              Undo
            </MuiButton>
          }
          autoHideDuration={null}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default CcaSplitPaymentTable;
